import { motion } from 'motion/react';
import { useMemo } from 'react';

import { Badge } from '@components/Badge';
import { useAccount } from '@hooks/useAccount';
import { AppPage } from '@hooks/useNavigation';

import { Header } from './shared/Header';

interface ProfileScreenProps {
  showSideMenu: boolean;
  toggleSideMenu: () => void;
  totalPositions: number;
  onNavigate: (page: AppPage) => void;
  advancedMode: boolean;
  animatePositionsPill?: boolean;
}

export const ProfileScreen = ({
  totalPositions,
  onNavigate,
}: ProfileScreenProps) => {
  // Get real account data
  const { balance, accountValue, openPnl, ledger } = useAccount();

  // Calculate trading statistics from ledger
  const stats = useMemo(() => {
    const buys = ledger.filter(tx => tx.type === 'BUY_OPTION');
    const closes = ledger.filter(
      tx => tx.type === 'FILL_ORDER' || tx.type === 'EXPIRE_POSITION',
    );

    const totalTrades = buys.length;
    const completedTrades = closes.length;

    // Calculate PnL from completed trades
    let totalPnL = 0;
    let wins = 0;
    let winPnL = 0;
    let lossPnL = 0;

    closes.forEach(closeTx => {
      if (closeTx.meta?.pnl !== undefined) {
        const pnl = closeTx.meta.pnl as number;
        totalPnL += pnl;
        if (pnl > 0) {
          wins++;
          winPnL += pnl;
        } else {
          lossPnL += Math.abs(pnl);
        }
      }
    });

    const winRate = completedTrades > 0 ? (wins / completedTrades) * 100 : 0;
    const avgWin = wins > 0 ? winPnL / wins : 0;
    const avgLoss =
      completedTrades - wins > 0 ? lossPnL / (completedTrades - wins) : 0;

    return {
      totalTrades,
      winRate,
      avgWin,
      avgLoss,
      totalPnL,
      completedTrades,
    };
  }, [ledger]);

  const formatPrice = (price: number) => {
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="bg-brand-dark min-h-screen w-full flex flex-col">
      {/* Shared Header */}
      <Header asset="PROFILE" title="PROFILE" onNavigate={onNavigate} />

      {/* Main Content */}
      <div className="flex-1 w-full">
        <div className="flex flex-col gap-3 p-4 w-full">
          {/* User Profile Header */}
          <div className="bg-white/5 rounded-2xl p-4 w-full">
            <div className="flex flex-row items-center gap-4">
              <div className="w-16 h-16 aspect-square rounded-full overflow-hidden border-2 border-brand-green flex-shrink-0">
                <img
                  src={'https://avatars.githubusercontent.com/u/273937?v=4'}
                  alt="Micah Alcorn"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="flex flex-col gap-1">
                <h2 className="text-[#ffffff] text-[18px] font-semibold">
                  Micah Alcorn
                </h2>
                <p className="text-[#808080] text-[12px]">
                  {stats.totalTrades > 0
                    ? `Active Trader • ${stats.totalTrades} trades`
                    : 'New Trader'}{' '}
                  • Member since Jan 2024
                </p>
                <div className="flex flex-row gap-2 items-center">
                  <Badge className="bg-white/10 text-white text-[10px] px-2 py-1">
                    {stats.totalTrades === 0 ? 'New User' : 'Active'}
                  </Badge>
                  <Badge className="bg-white/10 text-white text-[10px] px-2 py-1">
                    Verified
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
            {/* Portfolio Value */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex flex-col gap-1">
                <p className="text-[#808080] text-[12px]">Portfolio Value</p>
                <p className="text-[#ffffff] text-[20px] font-bold">
                  {formatPrice(accountValue)}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-[#808080] text-[11px]">
                    Available Balance: {formatPrice(balance)}
                  </span>
                </div>
              </div>
            </div>

            {/* Total PnL */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex flex-col gap-1">
                <p className="text-[#808080] text-[12px]">Total PnL</p>
                <p
                  className={`text-[20px] font-bold ${stats.totalPnL >= 0 ? 'text-brand-green' : 'text-brand-red'}`}
                >
                  {formatPrice(stats.totalPnL)}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-[#808080] text-[11px]">
                    Open PnL: {formatPrice(openPnl)}
                  </span>
                </div>
              </div>
            </div>

            {/* Win Rate */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex flex-col gap-1">
                <p className="text-[#808080] text-[12px]">Win Rate</p>
                <p className="text-[#ffffff] text-[20px] font-bold">
                  {stats.completedTrades > 0
                    ? `${stats.winRate.toFixed(1)}%`
                    : '--%'}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-[#808080] text-[11px]">
                    {stats.completedTrades} completed trades
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white/5 rounded-2xl p-4 w-full">
            <h3 className="text-[#ffffff] text-[14px] font-semibold mb-3">
              Quick Stats
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-[#808080] text-[11px]">Total Trades</p>
                <p className="text-[#ffffff] text-[13px] font-medium">
                  {stats.totalTrades}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[#808080] text-[11px]">Active Positions</p>
                <p className="text-[#ffffff] text-[13px] font-medium">
                  {totalPositions}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[#808080] text-[11px]">Avg Win</p>
                <p className="text-brand-green text-[13px] font-medium">
                  {stats.avgWin > 0 ? formatPrice(stats.avgWin) : '--'}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[#808080] text-[11px]">Avg Loss</p>
                <p className="text-brand-red text-[13px] font-medium">
                  {stats.avgLoss > 0 ? formatPrice(stats.avgLoss) : '--'}
                </p>
              </div>
            </div>
          </div>

          {/* Get Started or Performance Section */}
          {stats.totalTrades === 0 ? (
            <div className="bg-white/5 rounded-2xl p-4 w-full">
              <div className="text-center">
                <div className="text-[#ffffff] text-[14px] font-semibold mb-2">
                  Ready to start trading?
                </div>
                <div className="text-[#808080] text-[12px] mb-3">
                  Your balance: {formatPrice(balance)} • No trades yet
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <motion.button
                    className="border border-brand-green bg-brand-green/20 text-brand-green rounded-lg px-4 py-2 font-semibold text-[12px] hover:bg-brand-green/30 transition-colors"
                    onClick={() => onNavigate('ethmarket')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Trade ETH Options
                  </motion.button>
                  <motion.button
                    className="bg-white/10 border border-[#808080] rounded-lg px-4 py-2 font-semibold text-white text-[12px] hover:bg-white/15 transition-colors"
                    onClick={() => onNavigate('btcmarket')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Trade BTC Options
                  </motion.button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white/5 rounded-2xl p-4 w-full">
              <h3 className="text-[#ffffff] text-[14px] font-semibold mb-3">
                Performance Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between">
                    <span className="text-[#808080] text-[12px]">
                      Portfolio Value:
                    </span>
                    <span className="text-[#ffffff] text-[12px] font-medium">
                      {formatPrice(accountValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#808080] text-[12px]">
                      Realized PnL:
                    </span>
                    <span
                      className={`text-[12px] font-medium ${stats.totalPnL >= 0 ? 'text-brand-green' : 'text-brand-red'}`}
                    >
                      {formatPrice(stats.totalPnL)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#808080] text-[12px]">
                      Open PnL:
                    </span>
                    <span
                      className={`text-[12px] font-medium ${openPnl >= 0 ? 'text-brand-green' : 'text-brand-red'}`}
                    >
                      {formatPrice(openPnl)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between">
                    <span className="text-[#808080] text-[12px]">
                      Total Trades:
                    </span>
                    <span className="text-[#ffffff] text-[12px] font-medium">
                      {stats.totalTrades}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#808080] text-[12px]">
                      Win Rate:
                    </span>
                    <span className="text-[#ffffff] text-[12px] font-medium">
                      {stats.completedTrades > 0
                        ? `${stats.winRate.toFixed(1)}%`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#808080] text-[12px]">
                      Active Positions:
                    </span>
                    <span className="text-[#ffffff] text-[12px] font-medium">
                      {totalPositions}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
